import React from 'react';
import {NavLink,Outlet,useNavigate} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlobalSearch from './GlobalSearch.jsx';
import ShareModal from './ShareModal.jsx';
import ShareBroadcaster from './ShareBroadcaster.jsx';
import {useSyncExternalStore} from 'react';
import {subscribeShareHost, getShareHostState} from '../services/shareHostStore.js';
import {useWorkspace} from '../hooks/useWorkspace';
import {dataClient} from '../data/dataClient';
import {getUsageSummary} from '../services/aoMeter';

const {FiCommand,FiDownload,FiMenu,FiX,FiLogOut,FiShare2}=FiIcons;

function Navigation({modules,compact,close,prefix}) {
  return (
    <nav className={compact ? 'bottom-nav' : 'side-nav'} aria-label="Main navigation">
      {modules.map((module)=> (
        <NavLink
          key={module.slug}
          to={`${prefix}/${module.slug}`}
          onClick={close}
          className={({isActive})=> isActive ? 'nav-link active' : 'nav-link'}
        >
          <SafeIcon name={module.icon} />
          <span>{module.name}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppShell({installPrompt,onInstall,workspace='pb',companySlug='workspace'}) {
  const {business,modules,session}=useWorkspace();
  const [menuOpen,setMenuOpen]=React.useState(false);
  const [searchOpen,setSearchOpen]=React.useState(false);
  const [shareOpen,setShareOpen]=React.useState(false);
  const shareHost=useSyncExternalStore(subscribeShareHost,getShareHostState);
  const navigate=useNavigate();
  const usage=getUsageSummary();
  const prefix=`/workspace/${companySlug}`;

  async function logout() {
    dataClient.signOut();
    navigate('/signin',{replace:true});
  }

  return (
    <div className="app-shell">
      <aside className={menuOpen ? 'sidebar open' : 'sidebar'}>
        <div className="brand">
          <span><SafeIcon icon={FiCommand} /></span>
          <div>
            <b>Command</b>
            <small>{business.name}</small>
          </div>
        </div>

        <button
          className="mobile-close icon-button"
          onClick={()=> setMenuOpen(false)}
        >
          <SafeIcon icon={FiX} />
        </button>

        <Navigation modules={modules} close={()=> setMenuOpen(false)} prefix={prefix} />

        <div className="user-card">
          <span className="avatar dark">{session.user?.name?.[0]}</span>
          <div>
            <b>{session.user?.name}</b>
            <small>{session.user?.role}</small>
          </div>
          <button
            className="logout-button icon-button"
            onClick={logout}
            title="Sign out"
            aria-label="Sign out"
          >
            <SafeIcon icon={FiLogOut} />
          </button>
        </div>
        {workspace==='demo' && (
          <small className="demo-badge">Dev demo — connect a provider to try live chat</small>
        )}
      </aside>

      {/* Persistent HOST mirror: route changes + screen frames keep flowing to
          viewers even when the share panel is closed (post-launch fix). */}
      <ShareBroadcaster businessName={business?.name||companySlug} />

      <main className="main-area">
        <header className="topbar">
          <button
            className="menu-button icon-button"
            onClick={()=> setMenuOpen(true)}
          >
            <SafeIcon icon={FiMenu} />
          </button>

          <div>
            <small>Workspace</small>
            <b>{business.name}</b>
          </div>

          <GlobalSearch
            isOpen={searchOpen}
            onOpen={()=> setSearchOpen(true)}
            onClose={()=> setSearchOpen(false)}
          />

          <span className="ao-chip" title={`${usage.used} of ${usage.allowance} Agent Operations used this period`}>
            {usage.remaining} AO left
          </span>

          {/* T12: persistent HOST share action in the app chrome (topbar).
              Reflects the true broadcast state (post-launch fix: the old
              button looked identical whether or not a share was live). */}
          <button
            className={`button secondary compact share-topbar-button ${shareHost.shareId ? (shareHost.paused ? 'share-paused' : 'share-live') : ''}`}
            onClick={()=> setShareOpen(true)}
            title={shareHost.shareId
              ? (shareHost.paused ? 'Sharing paused — click to manage' : 'Sharing is LIVE — click to manage or stop')
              : 'Share your workspace live with a viewer-guest'}
          >
            <SafeIcon icon={FiShare2} />
            Share
            {shareHost.shareId && (
              <span className={`share-state-chip ${shareHost.paused ? 'paused' : ''}`}>
                {shareHost.paused ? 'paused' : 'live'}
              </span>
            )}
          </button>

          {installPrompt && (
            <button className="button secondary compact" onClick={onInstall}>
              <SafeIcon icon={FiDownload} />
              Install app
            </button>
          )}
        </header>

        <Outlet />
      </main>

      {shareOpen && <ShareModal onClose={()=> setShareOpen(false)} />}

      <Navigation modules={modules.slice(1,6)} compact prefix={prefix} />

      {menuOpen && (
        <button
          className="scrim"
          aria-label="Close navigation"
          onClick={()=> setMenuOpen(false)}
        />
      )}
    </div>
  );
}